import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { PageHeader } from '~/components/Navigation/PageHeader'
import { StalenessIndicator } from '~/components/Navigation/StalenessIndicator'
import { DimFilterPanel } from '~/components/Filters/DimFilterPanel'
import { TimeRangePicker } from '~/components/Filters/TimeRangePicker'
import { KpiCascade } from '~/components/Filters/KpiCascade'
import {
  TimeframeSelect,
  toAbsoluteTimeframes,
  toSurfacedRollingTimeframes,
} from '~/components/Filters/TimeframeSelect'
import { GranularityControls } from '~/components/Filters/GranularityControls'
import { ResetSettingsButton } from '~/components/Filters/ResetSettingsButton'
import { MultiTimeframePicker } from '~/components/Filters/MultiTimeframePicker'
import { ComparisonSwitch } from './ComparisonSwitch'
import { CoarseningBadge } from './CoarseningBadge'
import { ControlCard } from './ControlCard'
import { shouldShowPanelBase } from './showPanelBase'
import { PanelBaseCheckbox } from './PanelBaseCheckbox'
import { usePageControls } from '~/api/hooks/useCharts'
import { useSyncModelFloor } from '~/state/useSyncModelFloor'
import { useModelFloorIso } from '~/state/modelFloor'
import {
  buildCampaignSelectionParams,
  CAMPAIGN_MANAGED_DIMS,
  predicateOf,
} from '~/state/campaignPredicate'
import type { ReviewPageState } from './useReviewPageState'
import type { ComparisonState } from './useComparisonState'
import {
  MESSAGES,
  CONTROLS,
  prunedDimsNotice,
  campaignPrunedNotice,
  removedKpiNotice,
} from '~/components/Charts/chrome/chartChrome'
// [round-3 port] master retired these CARDS keys with the ControlBand redesign; release's scaffold still needs them.
import { LEGACY_CONTROLS } from '~/components/Charts/chrome/legacyControlCopy'

interface Props {
  title: string
  state: ReviewPageState
  compare?: ComparisonState
  showComparison?: boolean
  showGranularity?: boolean
  /** FU-f: page's registry tile set includes dynamics_panels. */
  panelBearing?: boolean
  /** Row 21: roots already rendered by the page's tiles (e.g. the Dynamics driver row);
   *  ComparisonSwitch excludes them from the metric-compare DEFAULT pick. */
  compareExcludedRoots?: string[]
  /** Page-specific control(s) rendered in the control row after the KPI cascade
   *  (e.g. the Contribution view toggle). */
  extraControls?: ReactNode
  children: ReactNode
}

export function ReviewPageScaffold({
  title,
  state,
  compare,
  showComparison = true,
  showGranularity = true,
  panelBearing = false,
  compareExcludedRoots,
  extraControls,
  children,
}: Props) {
  const controls = usePageControls(state.factTable)
  // Sync the model floor from get_controls into the global store (single mount point).
  useSyncModelFloor(controls.data)
  const floorIso = useModelFloorIso()
  // Keep only items with both bounds so they can drive a selection.
  const campaignItems = (controls.data?.campaign_timeslots?.items ?? []).filter(
    (i) => i.from && i.to,
  )
  const frameOptions = toAbsoluteTimeframes(controls.data?.timeframe_frames?.items)
  const rollingOptions = toSurfacedRollingTimeframes(controls.data?.timeframe_rolling?.items)

  // Row 15-light: the previous-period baseline checkbox shows only where it is APPLICABLE —
  // granularity visible, compare off (compare suppresses panel mode), the window actually
  // partitions into ≥2 panels, and no active multi-timeframe selection (per-TF panels).
  // FU-f (operator 2026-06-11: EXPOSE): panel-bearing pages (registry tile set includes
  // dynamics_panels) are exempt from the compare-suppression — sub-period panels and compare
  // rows legitimately coexist on Dynamics. ReviewKpis-class pages keep the hide.
  // An inapplicable URL-carried panel_base stays silently inert (state-level gate, unchanged).
  const multiTfActive = Boolean(state.multiTimeframe && state.multiTimeframe.entries.length > 0)
  const showPanelBase = shouldShowPanelBase({
    showGranularity,
    compareMode: compare ? compare.mode : null,
    panelBearing,
    dots: state.granularity.dots,
    windowBuckets: state.windowBuckets,
    multiTfActive,
  })

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      <PageHeader title={title} actions={<ResetSettingsButton />} />
      <Stack spacing={2}>
        {state.kpiCascade.controlsError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={state.kpiCascade.retryControls}>
                {MESSAGES.retry}
              </Button>
            }
          >
            {MESSAGES.controlsLoadFailed}
          </Alert>
        ) : state.kpiCascade.controlsEmpty ? (
          <Alert severity="warning">{MESSAGES.noKpisConfigured}</Alert>
        ) : null}
        <Stack direction="row" spacing={2} alignItems="stretch" flexWrap="wrap" useFlexGap>
          <ControlCard id="kpi" title={LEGACY_CONTROLS.kpiTitle} helper={LEGACY_CONTROLS.kpiHelper}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
              <KpiCascade state={state.kpiCascade} />
              {extraControls}
            </Stack>
          </ControlCard>

          <ControlCard
            id="timeframe"
            title={LEGACY_CONTROLS.timeframeTitle}
            helper={LEGACY_CONTROLS.timeframeHelper}
            flex={1.4}
            minWidth={340}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                <TimeframeSelect
                  preset={state.timeframePreset.preset}
                  fromIso={state.fromIso}
                  toIso={state.toIso}
                  frames={frameOptions}
                  rolling={rollingOptions}
                  campaignItems={campaignItems}
                  onSelectPreset={state.timeframePreset.setPreset}
                  onSelectAbsolute={(from, to) =>
                    state.setMultipleSearchParams({ from, to, tf_preset: 'custom' })
                  }
                  onSelectCampaign={(item) =>
                    state.setMultipleSearchParams(buildCampaignSelectionParams(item))
                  }
                  activeCampaignId={state.campaign?.id ?? null}
                />
                <TimeRangePicker
                  fromIso={state.fromIso}
                  toIso={state.toIso}
                  minDate={floorIso ? new Date(floorIso) : undefined}
                  onChange={({ fromIso: f, toIso: t }) =>
                    // A manual date edit means a custom window — neutralize the active preset, else
                    // its computedRange overrides the typed dates (effectiveFrom = computedRange ??
                    // fromIso).
                    state.setMultipleSearchParams({ from: f, to: t, tf_preset: 'custom' })
                  }
                />
              </Stack>
              {showGranularity ? (
                <Box>
                  <GranularityControls
                    unit={state.granularity.unit}
                    dots={state.granularity.dots}
                    onUnitChange={state.granularity.setUnit}
                    onDotsChange={state.granularity.setDots}
                  />
                  <CoarseningBadge governance={state.governance} />
                </Box>
              ) : null}
              {state.multiTimeframe ? (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {LEGACY_CONTROLS.additionalTimeframes}
                  </Typography>
                  <MultiTimeframePicker
                    entries={state.multiTimeframe.entries}
                    // Rolling frames intentionally excluded here — the multi-TF overlay is a
                    // parked surface (WP-C out of scope); only fixed frames + campaigns are
                    // offered. Campaign options carry their id + predicate flag (Task 6) so
                    // added entries round-trip the campaign id (Task 7 resolves per-arm).
                    options={[
                      ...frameOptions,
                      ...campaignItems.map((i) => ({
                        value: i.value,
                        label: i.label,
                        from: i.from!,
                        to: i.to!,
                        campaign: i.value,
                        hasPredicate: Object.keys(predicateOf(i)).length > 0,
                      })),
                    ]}
                    onAdd={state.multiTimeframe.add}
                    onRemove={state.multiTimeframe.remove}
                  />
                </Box>
              ) : null}
            </Stack>
          </ControlCard>

          {(compare && showComparison) || showPanelBase ? (
            <ControlCard
              id="comparison"
              title={LEGACY_CONTROLS.comparisonTitle}
              helper={LEGACY_CONTROLS.comparisonHelper}
            >
              <Stack spacing={2}>
                {compare && showComparison ? (
                  <ComparisonSwitch
                    state={compare}
                    rootOptions={state.kpiCascade.rootOptions}
                    primaryRoot={state.kpiCascade.root}
                    excludedDefaultRoots={compareExcludedRoots}
                  />
                ) : null}
                {compare && showComparison && compare.mode === 'period' ? (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.5 }}
                    >
                      {CONTROLS.comparisonPeriod}
                    </Typography>
                    <TimeRangePicker
                      fromIso={compare.fromIso}
                      toIso={compare.toIso}
                      onChange={({ fromIso: f, toIso: t }) => compare.setRange(f, t)}
                    />
                  </Box>
                ) : null}
                {showPanelBase ? (
                  <PanelBaseCheckbox value={state.panelBase} onChange={state.setPanelBase} />
                ) : null}
              </Stack>
            </ControlCard>
          ) : null}

          <ControlCard
            id="dimensions"
            title={LEGACY_CONTROLS.dimensionsTitle}
            helper={LEGACY_CONTROLS.dimensionsHelper}
            flex={1.2}
            minWidth={320}
          >
            <Stack spacing={1}>
              <DimFilterPanel
                factTable={state.factTable}
                kpi={state.kpi}
                pageId={state.pageId}
                defaultGroupBy={state.groupByDefault}
                campaign={state.campaign}
              />
            </Stack>
          </ControlCard>
        </Stack>

        {state.prunedDims ? (
          <Alert severity="info" onClose={state.dismissPrunedNotice}>
            {/* Campaigns C1 (spec §4): campaign-aware copy only when ALL pruned dims are the
                campaign's managed dims; a mixed prune keeps the generic copy naming every dim
                (honest superset). */}
            {state.campaign &&
            state.prunedDims.every((d) => (CAMPAIGN_MANAGED_DIMS as readonly string[]).includes(d))
              ? campaignPrunedNotice(state.campaign.label)
              : prunedDimsNotice(state.prunedDims)}
          </Alert>
        ) : null}
        {/* FU-31: a cross-page-carried kpi_root the page couldn't resolve was reset to the page
            default — one dismissible notice names it (backstop V6 noChartsForKpiNote stays). */}
        {state.removedKpi ? (
          <Alert severity="info" onClose={state.dismissRemovedKpiNotice}>
            {removedKpiNotice(state.removedKpi)}
          </Alert>
        ) : null}
        <StalenessIndicator />
        {state.timeframeReady ? (
          children
        ) : state.kpiCascade.controlsError ? null : state.dimCatalogError ? (
          <Alert severity="error">{MESSAGES.controlsLoadFailed}</Alert>
        ) : controls.isError ? (
          <Alert severity="error">{MESSAGES.controlsLoadFailed}</Alert>
        ) : state.ratioComponentControlsError ? (
          // C3b M1: a ratio page's component-fact controls settled in error under an active campaign
          // — the page fact's own controls succeeded, so surface the real error loudly here instead
          // of hanging on the misleading "no date range" warning below (readiness never lifts).
          <Alert severity="error">{MESSAGES.controlsLoadFailed}</Alert>
        ) : controls.isSuccess ? (
          <Alert severity="warning">{MESSAGES.noDateRangeForFact}</Alert>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}
      </Stack>
    </Box>
  )
}
